import { useEffect, useRef } from "react";
import { AppConfig } from "../../Config/app-config";
import { useNotification } from "../../Hooks/useNotification/useNotification";
import { ChatbotConversationService } from "../../Services/Apis/ChatbotConversationService";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
  removeAllMessages,
  setLastNodeResponse,
  setNewAllMessages,
  setOriginalResponses,
  setUserInfoData
} from "../../Store/Slices/socket/AllMessagesSlice";
import { queryClient } from "../../Utils/tanstack-query";
import { getChatSessionFilterPayloadData } from "../utils/utils";
import {
  updateQueryCacheInPlace
} from "./cacheHelpers";
import { useSocket } from "./socket-context";
import { whatsappStatusFields } from "../context/WhatsappMessageStatusContext";

const useMessagesData = () => {
  //redux
  const dispatch = useAppDispatch();
  const { showAlert } = useNotification();

  const { socket } = useSocket();

  const data = AppConfig.getLoggedInData();;
  const username = data?.data?.user?.id;

  const sessionId = useAppSelector(
    (state) => state.currentAllMessagesStore.sessionId
  );
  // Create a ref for sessionId
  const sessionIdRef = useRef(sessionId);
  // Update the ref whenever sessionId changes to avoid closures scopes below.
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const chatSessionActiveFilter = useAppSelector(
    (state) => state.chat.data.chatSessionActiveFilter
  );
  const filtersRef = useRef<any>();

  useEffect(() => {
    if (!chatSessionActiveFilter) return;
    filtersRef.current = getChatSessionFilterPayloadData(
      chatSessionActiveFilter
    );
  }, [chatSessionActiveFilter]);


  const handleHistoryMessages = async (history: any) => {
    let payload: any = [];
    if (
      history?.chat_data?.length === 0 ||
      !Array.isArray(history?.chat_data)
    ) {
      console.error("No chat data found", history);
      showAlert("No Chat Data found.", "error");
      return;
    }
    history?.chat_data?.forEach((item: any, index: any) => {
      if (item?.["user_query_object"]) {
        let temp = {
          ...item?.["user_query_object"],
          time: item?.["createdAt"],
          message_id: item?.["message_id"],
          delivered: item?.["delivered"],
          seen: item?.["seen"],
          sent: item?.["sent"],
        };
        payload.push(temp);
      }

      if (item?.["bot_response"]?.["response"]) {
        item?.["bot_response"]?.["response"]?.forEach((curRes: any) => {
          let temp = {
            ...curRes,
            time: item?.["createdAt"],
            message_id: item?.["message_id"],
            delivered: item?.["delivered"],
            seen: item?.["seen"],
            sent: item?.["sent"],
            ...whatsappStatusFields(item),
          };
          payload.push(temp);
        });
      }

      if (index + 1 === history?.chat_data?.length) {
        dispatch(setLastNodeResponse(item?.["bot_response"]));
      }
      dispatch(setOriginalResponses(item?.["bot_response"]));
    });

    dispatch(setNewAllMessages(payload));

    //* set visitor data
  };

    const handleUserInfoData = (data: any) => {
    const newData = { ...data, count: 0 };
    dispatch(setUserInfoData(newData));
    const queryKey = [
      "conversations",
      "sessions",
      "listing",
      filtersRef.current,
    ];
    updateQueryCacheInPlace(queryKey, newData);
  };

  const sendUserSessionInfoRequestEvent = (
    id: string,
    botid: string = ""
  ): any => {
    if (!id) {
      showAlert("Invalid session ID", "error");
      return;
    }

    const queryKey = ["conversations", "chat-history", id, "userinfo"];
    const cached: any = queryClient.getQueryData(queryKey);

    return queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        ChatbotConversationService.getUserSessionInfo(
          {
            session_id: id,
            user_id: username,
          },
          {
            connection_type: "agent",
          }
        )
          .then((response) => {
            if (!response?.data?.data?.session_id) return;
            const newData = { ...response?.data?.data, count: 0 };
            handleUserInfoData(newData);
            return response?.data?.data ?? null;
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {}),

      staleTime: 0,
      initialData: cached,
    });
  };


  return {
    sendUserSessionInfoRequestEvent,
    handleUserInfoData
  };
};

export default useMessagesData;
